/**
 * CeoCalendarPage — Phase 36.7b.
 *
 * Founder schedule snapshot surface.
 * Endpoint: GET /api/v1/admin/ceo/calendar/snapshot
 * Poll: 15 min (matches server-side TTL).
 *
 * AD-097 PII compliance:
 *   - Calendar data is NEVER written to localStorage.
 *   - Calendar data is NEVER sent to analytics.
 *   - Only in-memory TanStack Query cache (session-scoped).
 *   - No `staleTime` override — default 0 ensures no accidental persistence.
 *
 * When not connected (HTTP 503): shows OAuth connect button that
 * fetches the authorize URL and redirects the founder's browser.
 */

import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { isOmegaAdmin } from '@/features/omega/adminMode';
import { Breadcrumbs } from '@/features/omega/Breadcrumbs';
import { apiFetch, ApiHttpError } from '@/api/http';

// ── Types ────────────────────────────────────────────────────────────────────

export interface CalendarEvent {
  title: string;
  start_unix: number;
  end_unix: number;
  attendee_count: number;
  is_recurring: boolean;
}

export interface TimeBlock {
  start_unix: number;
  end_unix: number;
  duration_mins: number;
}

export interface SuggestedSlot {
  start_unix: number;
  label: string;
}

export interface FounderScheduleSnapshot {
  fetched_at: number;
  today_meetings: CalendarEvent[];
  week_commitments: CalendarEvent[];
  free_blocks: TimeBlock[];
  suggested_slots: SuggestedSlot[];
}

interface CalendarAuthResponse {
  authorize_url: string;
  state: string;
}

// ── Helpers (exported for tests) ─────────────────────────────────────────────

const IDT_OFFSET_SECS = 3 * 3600;

export function fmtEventTime(startUnix: number, endUnix: number): string {
  const startIdt = new Date((startUnix + IDT_OFFSET_SECS) * 1000);
  const endIdt   = new Date((endUnix   + IDT_OFFSET_SECS) * 1000);
  const fmt = (d: Date) =>
    `${String(d.getUTCHours()).padStart(2, '0')}:${String(d.getUTCMinutes()).padStart(2, '0')}`;
  return `${fmt(startIdt)}–${fmt(endIdt)} IDT`;
}

export function fmtFreeBlock(durationMins: number): string {
  if (durationMins >= 60) {
    const h = Math.floor(durationMins / 60);
    const m = durationMins % 60;
    return m > 0 ? `${h}h ${m}m` : `${h}h`;
  }
  return `${durationMins}m`;
}

export function eventDurationMins(startUnix: number, endUnix: number): number {
  return Math.round((endUnix - startUnix) / 60);
}

export function fmtFetchedAt(secs: number): string {
  return new Date(secs * 1000).toLocaleString('he-IL', {
    day: '2-digit', month: '2-digit',
    hour: '2-digit', minute: '2-digit',
  });
}

export function fmtEventDate(startUnix: number): string {
  return new Date((startUnix + IDT_OFFSET_SECS) * 1000).toLocaleDateString('he-IL', {
    weekday: 'short', day: '2-digit', month: '2-digit',
    timeZone: 'UTC',
  });
}

// ── Page ─────────────────────────────────────────────────────────────────────

export function CeoCalendarPage() {
  if (!isOmegaAdmin()) return <Navigate to="/" replace />;

  const [connecting, setConnecting] = useState(false);
  const [connectError, setConnectError] = useState<string | null>(null);

  const { data, isPending, isError, error } = useQuery({
    queryKey: ['ceo', 'calendar-snapshot'],
    queryFn: () => apiFetch<FounderScheduleSnapshot>('/admin/ceo/calendar/snapshot'),
    refetchInterval: 900_000,
    retry: false,
    // AD-097: no caching beyond session memory — gcTime 0 so data is
    // dropped when the component unmounts, not persisted anywhere.
    gcTime: 0,
  });

  const isNotConnected =
    isError && error instanceof ApiHttpError && error.status === 503;

  async function handleConnect() {
    setConnecting(true);
    setConnectError(null);
    try {
      const resp = await apiFetch<CalendarAuthResponse>('/admin/ceo/calendar/auth');
      window.location.href = resp.authorize_url;
    } catch (e) {
      setConnectError((e as Error).message);
      setConnecting(false);
    }
  }

  return (
    <div className="omega-shell" style={{ fontFamily: 'var(--omega-mono)', color: 'var(--omega-ink)', padding: '16px 20px' }}>
      <Breadcrumbs crumbs={[
        { label: 'OMEGA',  to: '/omega' },
        { label: 'CEO GI', to: '/omega/ceo' },
        { label: 'Schedule' },
      ]} />

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 18, marginTop: 8 }}>
        <h1 style={{ margin: 0, fontSize: '11pt', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', direction: 'ltr' }}>
          Schedule — Founder
        </h1>
        {data && (
          <span style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 3, fontSize: '8pt', padding: '1px 7px', color: 'var(--omega-ink-subtle)' }}>
            Updated {fmtFetchedAt(data.fetched_at)}
          </span>
        )}
      </div>

      {isPending && !isNotConnected && (
        <div style={{ color: 'var(--omega-ink-subtle)', fontSize: '9pt' }}>Loading schedule…</div>
      )}

      {/* Not connected — OAuth prompt */}
      {isNotConnected && (
        <div style={{
          background: 'rgba(125,211,252,0.06)',
          border: '1px solid rgba(125,211,252,0.2)',
          borderRadius: 8, padding: '24px 28px', maxWidth: 480,
        }}>
          <div style={{ fontSize: '10pt', fontWeight: 600, marginBottom: 8, direction: 'ltr' }}>
            Google Calendar Not connected
          </div>
          <div style={{ fontSize: '9pt', color: 'var(--omega-ink-subtle)', marginBottom: 18, direction: 'ltr', lineHeight: 1.6 }}>
            Connect your Google Calendar to view meetings, Free time and recommended slots for reviewing decisions.
          </div>
          {connectError && (
            <div style={{ color: '#f87171', fontSize: '8.5pt', marginBottom: 12, direction: 'ltr' }}>
              Error: {connectError}
            </div>
          )}
          <button
            disabled={connecting}
            onClick={handleConnect}
            style={{
              background: connecting ? 'rgba(125,211,252,0.06)' : 'rgba(125,211,252,0.12)',
              border: '1px solid rgba(125,211,252,0.4)',
              borderRadius: 5, color: '#7dd3fc', fontFamily: 'var(--omega-mono)',
              fontSize: '9pt', fontWeight: 700, padding: '8px 20px',
              cursor: connecting ? 'default' : 'pointer',
              opacity: connecting ? 0.6 : 1,
            }}
          >
            {connecting ? 'Connecting…' : 'Connect Google Calendar'}
          </button>
        </div>
      )}

      {isError && !isNotConnected && (
        <div style={{ color: '#f87171', fontSize: '9pt', direction: 'ltr' }}>
          Error: {(error as Error).message}
        </div>
      )}

      {data && (
        <>
          {/* Today's meetings */}
          <section style={{ marginBottom: 26 }}>
            <SectionLabel label={`Today's meetings (${data.today_meetings.length})`} />
            {data.today_meetings.length === 0 ? (
              <EmptyNote label="No meetings today" />
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxWidth: 620 }}>
                {data.today_meetings.map((ev, i) => (
                  <EventRow key={i} ev={ev} showDate={false} />
                ))}
              </div>
            )}
          </section>

          {/* This week's commitments */}
          <section style={{ marginBottom: 26 }}>
            <SectionLabel label={`This week (${data.week_commitments.length} Meetings)`} />
            {data.week_commitments.length === 0 ? (
              <EmptyNote label="No commitments this week" />
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxWidth: 620 }}>
                {data.week_commitments.map((ev, i) => (
                  <EventRow key={i} ev={ev} showDate />
                ))}
              </div>
            )}
          </section>

          {/* Free blocks */}
          {data.free_blocks.length > 0 && (
            <section style={{ marginBottom: 26 }}>
              <SectionLabel label={`Free time (${data.free_blocks.length} Windows)`} />
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {data.free_blocks.map((block, i) => (
                  <div key={i} style={{
                    background: 'rgba(52,211,153,0.06)',
                    border: '1px solid rgba(52,211,153,0.2)',
                    borderRadius: 4, padding: '6px 12px',
                    fontSize: '8.5pt', direction: 'ltr',
                  }}>
                    <span style={{ color: '#34d399', fontWeight: 700 }}>
                      {fmtFreeBlock(block.duration_mins)}
                    </span>
                    <span style={{ color: 'var(--omega-ink-subtle)', marginRight: 6 }}>
                      {fmtEventTime(block.start_unix, block.end_unix)}
                    </span>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Suggested decision review slots */}
          {data.suggested_slots.length > 0 && (
            <section style={{ marginBottom: 26 }}>
              <SectionLabel label="Recommended slots for reviewing decisions" />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxWidth: 500 }}>
                {data.suggested_slots.map((slot, i) => (
                  <div key={i} style={{
                    display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px',
                    background: 'rgba(125,211,252,0.04)',
                    border: '1px solid rgba(125,211,252,0.15)', borderRadius: 4,
                  }}>
                    <span style={{ color: '#7dd3fc', fontWeight: 700, fontSize: '9pt', fontFamily: 'var(--omega-mono)' }}>
                      {i + 1}
                    </span>
                    <span style={{ fontSize: '9pt', direction: 'ltr' }}>{slot.label}</span>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* PII notice */}
          <div style={{
            marginTop: 8, fontSize: '7.5pt', color: 'var(--omega-ink-subtle)',
            borderTop: '1px solid rgba(255,255,255,0.06)', paddingTop: 10, direction: 'ltr',
          }}>
            AD-097 — Schedule data is PII. Not saved, Not sent to analytics, Not stored outside session memory.
          </div>
        </>
      )}
    </div>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────────

function SectionLabel({ label }: { label: string }) {
  return (
    <div style={{
      fontSize: '8.5pt', fontWeight: 700, letterSpacing: '0.1em',
      textTransform: 'uppercase', color: 'var(--omega-ink)', marginBottom: 10,
      fontFamily: 'var(--omega-mono)', direction: 'ltr',
    }}>
      {label}
    </div>
  );
}

function EmptyNote({ label }: { label: string }) {
  return (
    <div style={{ fontSize: '9pt', color: 'var(--omega-ink-subtle)', fontStyle: 'italic', direction: 'ltr' }}>
      {label}
    </div>
  );
}

function EventRow({ ev, showDate }: { ev: CalendarEvent; showDate: boolean }) {
  const durationMins = eventDurationMins(ev.start_unix, ev.end_unix);
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: showDate ? '80px 110px 1fr 50px' : '110px 1fr 50px',
      gap: 10, alignItems: 'center', padding: '8px 12px',
      background: 'rgba(255,255,255,0.02)',
      border: '1px solid rgba(255,255,255,0.07)', borderRadius: 4,
    }}>
      {showDate && (
        <span style={{ fontSize: '8pt', color: 'var(--omega-ink-subtle)', direction: 'ltr' }}>
          {fmtEventDate(ev.start_unix)}
        </span>
      )}
      <span style={{ fontSize: '8.5pt', fontFamily: 'var(--omega-mono)', color: 'var(--omega-ink-subtle)' }}>
        {fmtEventTime(ev.start_unix, ev.end_unix)}
      </span>
      <span style={{ fontSize: '9pt', fontWeight: 500, direction: 'ltr', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {ev.is_recurring && (
          <span style={{ color: '#a78bfa', marginLeft: 6, fontSize: '8pt' }}>↻</span>
        )}
        {ev.title}
      </span>
      <span style={{ fontSize: '8pt', color: 'var(--omega-ink-subtle)', textAlign: 'right', fontFamily: 'var(--omega-mono)' }}>
        {durationMins}m
      </span>
    </div>
  );
}
